import {Component, ContentChild, HostListener, inject, Input, OnInit, TemplateRef} from '@angular/core';
import {LessonsService} from '@modules/activities';
import {BehaviorSubject, combineLatest, Observable, of, ReplaySubject, scan} from 'rxjs';
import {LessonGranuleEntity} from '@modules/activities/core/models';
import {debounceTime, map, mergeMap, startWith, take} from 'rxjs/operators';
import {AuthenticationService} from '@modules/authentication';
import {ActivitySearchOptions} from '../activity-search-options';

const LESSON_PER_PAGE = 12;
const MINIMUM_LESSON_THRESHOLD_BEFORE_AUTO_LOAD = 5;

type LessonWithParent = { lesson: LessonGranuleEntity, parentLesson: LessonGranuleEntity };

@Component({
    selector: 'app-lessons-search-viewer',
    templateUrl: './lessons-search-viewer.component.html',
    styleUrls: ['./lessons-search-viewer.component.scss'],
    standalone: false
})
export class LessonsSearchViewerComponent implements OnInit {

    @Input() activitySearchOptions!: ActivitySearchOptions;
    @ContentChild(TemplateRef) templateRef: TemplateRef<any>;
    public isLoading = true;
    private lessonsWithParent$: BehaviorSubject<LessonWithParent[]> = new BehaviorSubject<LessonWithParent[]>([]);
    public lessonsWithParentAcc$ = this.lessonsWithParent$.pipe(scan((acc, value) => [...acc, ...value], []));
    private lessonsService = inject(LessonsService);
    private authenticationService = inject(AuthenticationService);
    private userId = this.authenticationService.userData.id;
    private isUserLessonsLeft = true;
    private isModelLessonsLeft = true;
    private pageNumber = 1;
    private isShouldLoadNextPage$ = new ReplaySubject<void>(1);

    ngOnInit(): void {
        this.subscribeToLoadNextPage();
    }

    @HostListener('scroll', ['$event'])
    onScroll(event: Event): void {
        if (!this.isLoading && this.isNearEndOfScroll(event)) {
            this.isShouldLoadNextPage$.next();
        }
    }

    private subscribeToLoadNextPage(): void {
        this.isShouldLoadNextPage$.pipe(
            startWith(null),
            debounceTime(500)
        ).subscribe(() => {
            this.loadNextPage();
        });
    }

    private isNearEndOfScroll(event: Event): boolean {
        const element = event.target as HTMLElement;
        const scrollPosition = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;
        const limiteMargin = clientHeight;

        const nearEndThreshold = scrollHeight - clientHeight;
        const triggerThreshold = nearEndThreshold - limiteMargin;

        return scrollPosition >= triggerThreshold;
    }

    private loadNextPage(): void {
        this.isLoading = true;
        let lessonsObservable: Observable<{ lessons: LessonWithParent[], hasNextPage: boolean }>;
        const filters = this.generateFilters();

        if (this.isUserLessonsLeft) {
            lessonsObservable = this.getUserLessons(filters);
        } else if (this.isModelLessonsLeft) {
            lessonsObservable = this.getModelLessons(filters);
        } else {
            this.isLoading = false;
            return;
        }

        if (this.activitySearchOptions?.flattenSubLesson) {
            lessonsObservable = this.flattenLessons(lessonsObservable);
        }

        this.subscribeToLessonsObservable(lessonsObservable);
    }

    private flattenLessons(lessonsObservable: Observable<{ lessons: LessonWithParent[], hasNextPage: boolean }>): Observable<{
        lessons: LessonWithParent[],
        hasNextPage: boolean
    }> {
        return lessonsObservable.pipe(
            mergeMap(({lessons, hasNextPage}) => {
                return this.getFlattenedLessons(lessons).pipe(
                    map(flattenedLessons => ({lessons: flattenedLessons, hasNextPage}))
                );
            })
        );
    }

    private subscribeToLessonsObservable(lessonsObservable: Observable<{ lessons: LessonWithParent[], hasNextPage: boolean }>): void {
        lessonsObservable.pipe(take(1)).subscribe(({lessons, hasNextPage}) => {
            this.displayLessons(lessons);
            this.updatePageState(hasNextPage, lessons);
            this.isLoading = false;
        });
    }

    private displayLessons(lessons: LessonWithParent[]): void {
        const filteredLessons = lessons.filter(lesson => {
            return lesson.lesson.attributes.reference;
        });
        this.lessonsWithParent$.next(filteredLessons.slice());
    }

    private updatePageState(hasNextPage: boolean, currentLessons: LessonWithParent[]): void {
        if (!hasNextPage) {
            if (this.isUserLessonsLeft) {
                this.isUserLessonsLeft = false;
                this.isModelLessonsLeft = true;
                this.pageNumber = 1;
            } else if (this.isModelLessonsLeft) {
                this.isModelLessonsLeft = false;
            }
        } else {
            this.pageNumber++;
        }

        // S'il n'y a pas assez de leçons pour satisfaire l'ux et qu'il reste des leçons à charger, charger la page suivante
        if (currentLessons.length <= MINIMUM_LESSON_THRESHOLD_BEFORE_AUTO_LOAD && (this.isUserLessonsLeft || this.isModelLessonsLeft)) {
            this.loadNextPage();
        }
    }

    private getFlattenedLessons(LessonsWithParent: LessonWithParent[]): Observable<LessonWithParent[]> {
        // Si lessonsWithParents est vide, on ne fait rien mais on ne bloque pas le flux
        if (LessonsWithParent.length === 0) {
            return of([]);
        }

        const onlyNotParentLessonsAndSubLessons$: Observable<LessonWithParent>[] = [];

        for (const LessonWithParent of LessonsWithParent) {
            const references = LessonWithParent.lesson.get('reference');
            if (references.every(ref => ref.type !== 'lesson')) {
                onlyNotParentLessonsAndSubLessons$.push(of(LessonWithParent));
                continue;
            }

            onlyNotParentLessonsAndSubLessons$.push(
                ...references
                    .filter(r => r.type === 'lesson') // On ne gère pas les lesson qui on des sous lesson et des activités
                    .map(ref => this.lessonsService.loadLessonGranuleById(ref.id))
                    .map(lesson$ => lesson$.pipe(map(lesson => ({
                        lesson,
                        parentLesson: LessonWithParent.lesson
                    }))))
            );
        }

        return combineLatest(onlyNotParentLessonsAndSubLessons$).pipe(
            map(lessons => lessons.slice()),
        );
    }

    private getPaginatedLessons(filters: Partial<{
        concepts: string | number;
        level: string | number;
        chapters: string | number;
        author: string | number;
        role: number[];
    }>, pageNumber: number): Observable<{ lessons: LessonWithParent[], hasNextPage: boolean }> {

        const withoutSubLessonAsLesson = {multi_step: 0};
        const overrideFilters = {...withoutSubLessonAsLesson, ...filters};

        return this.lessonsService.loadPaginatedLessonGranuleCollection({
            filter: overrideFilters,
            range: LESSON_PER_PAGE,
            page: pageNumber
        }).pipe(
            map(({entities, paginator}) => {
                return {
                    lessons: entities.map(lesson => ({lesson, parentLesson: null})),
                    hasNextPage: paginator.hasNextPage
                };
            }),
        );
    }

    private getUserLessons(filters: Partial<{
        concepts: string | number;
        level: string | number;
        chapters: string | number;
        author: string | number;
    }>): Observable<{ lessons: LessonWithParent[], hasNextPage: boolean }> {
        filters['author'] = this.userId;
        return this.getPaginatedLessons(filters, this.pageNumber);
    }

    private getModelLessons(filters: Partial<{
        concepts: string | number;
        level: string | number;
        chapters: string | number;
        role: number[];
    }>): Observable<{ lessons: LessonWithParent[], hasNextPage: boolean }> {
        filters['role'] = this.lessonsService.getAllowedRoleIdsForModelsCreation();
        return this.getPaginatedLessons(filters, this.pageNumber);
    }

    private generateFilters(): Partial<{
        concepts: string | number,
        level: string | number,
        chapters: string | number,
    }> {
        const filters = {};

        if (this.activitySearchOptions?.concepts && this.activitySearchOptions.concepts.length) {
            filters['concepts'] = this.activitySearchOptions.concepts.map(c => c.id);
        }
        if (this.activitySearchOptions?.educationalLevels && this.activitySearchOptions.educationalLevels.length) {
            filters['level'] = this.activitySearchOptions.educationalLevels.map(e => e.id);
        }
        if (this.activitySearchOptions?.chapters && this.activitySearchOptions.chapters.length) {
            filters['chapters'] = this.activitySearchOptions.chapters.map(c => c.id);
        }
        return filters;
    }

    public hasMorePageToLoad(): boolean {
        return this.isUserLessonsLeft || this.isModelLessonsLeft;
    }
}
